import { api } from '@techsofit/app-kit';
import type {
  MemberMe,
  MemberWeek,
  MemberStats,
  SelfWorkoutRequest,
  CompletionRequest,
  Exercise,
  ExerciseFilters,
  Goal,
  Wrapped,
  WrappedShare,
  MemberSubscription,
  MemberNotification,
  MyGuestPasses,
  GuestPass,
  CreateGuestPassRequest,
  CheckInResult,
  MemberCheckInStatus,
} from '@techsofit/api-types';
import type { ExerciseQuery } from './gymApi';

/** Member-portal API (member OTP session, separate from staff auth). */

/** `next` tells the email's one-tap button where to land ("/checkin" or "/me"). */
export const requestMemberOtp = (contact: string, next?: '/checkin' | '/me') =>
  api<{ found: boolean }>('/member-auth/request-otp', { method: 'POST', body: { contact, next } });

export const verifyMemberOtp = (contact: string, code: string) =>
  api<MemberMe>('/member-auth/verify-otp', { method: 'POST', body: { contact, code } });

/** One-tap sign-in from the email button (`?lt=` token). Single use. */
export const verifyMemberLink = (token: string) =>
  api<MemberMe>('/member-auth/verify-link', { method: 'POST', body: { token } });

export const getMemberMe = () => api<MemberMe>('/member-auth/me');

export const memberLogout = () => api<void>('/member-auth/logout', { method: 'POST' });

export const getMemberWeek = (from: string, to: string) =>
  api<MemberWeek>(`/member-portal/week?from=${from}&to=${to}`);

export const createSelfWorkout = (req: SelfWorkoutRequest) =>
  api<void>('/member-portal/workouts', { method: 'POST', body: req });

export const deleteSelfWorkout = (id: string) =>
  api<void>(`/member-portal/workouts/${id}`, { method: 'DELETE' });

export const setMemberCompletion = (req: CompletionRequest) =>
  api<void>('/member-portal/completion', { method: 'POST', body: req });

export function getMemberExercises(p: ExerciseQuery = {}) {
  const qs = new URLSearchParams();
  if (p.muscleGroup) qs.set('muscleGroup', p.muscleGroup);
  if (p.equipment) qs.set('equipment', p.equipment);
  if (p.category) qs.set('category', p.category);
  if (p.q) qs.set('q', p.q);
  const s = qs.toString();
  return api<Exercise[]>(`/member-portal/exercises${s ? `?${s}` : ''}`);
}

export const getMemberExerciseFilters = () => api<ExerciseFilters>('/member-portal/exercises/filters');

// ── Progress: stats, goals, Glow Up ──────────────────────────────────────────

export const getMemberStats = () => api<MemberStats>('/member-portal/stats');

export const getMemberGoals = () => api<Goal[]>('/member-portal/goals');

export const getMySubscriptions = () => api<MemberSubscription[]>('/member-portal/subscriptions');

export const getMyGlowUp = () => api<Wrapped>('/member-portal/wrapped');

export const shareMyGlowUp = () => api<WrappedShare>('/member-portal/wrapped/share', { method: 'POST' });

// ── In-app notifications ──────────────────────────────────────────────────────

export const getMyNotifications = () => api<MemberNotification[]>('/member-portal/notifications');

export const getMyUnreadCount = () =>
  api<{ count: number }>('/member-portal/notifications/unread-count');

export const markNotificationRead = (id: string) =>
  api<void>(`/member-portal/notifications/${id}/read`, { method: 'POST' });

export const markAllNotificationsRead = () =>
  api<void>('/member-portal/notifications/read-all', { method: 'POST' });

// ── Guest passes (member) ─────────────────────────────────────────────────────

export const getMyGuestPasses = () => api<MyGuestPasses>('/member-portal/guest-passes');

export const inviteGuest = (req: CreateGuestPassRequest) =>
  api<GuestPass>('/member-portal/guest-passes', { method: 'POST', body: req });

// ── Self check-in (gym QR / kiosk) ────────────────────────────────────────────

export const getMyCheckInStatus = () => api<MemberCheckInStatus>('/member-portal/check-in');

export const selfCheckIn = () => api<CheckInResult>('/member-portal/check-in', { method: 'POST' });
